import { useEffect, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/shared/lib/cn'
import { useSwipeDown } from '@/shared/hooks/useSwipeDown'

const modalOverlayVariants = cva(
  'fixed inset-0 z-[110] flex items-center justify-center bg-black/60 backdrop-blur-sm animate-in fade-in duration-200'
)

const modalContentVariants = cva(
  'w-full bg-[#111111] border border-white/5 rounded-xl shadow-2xl animate-in zoom-in-95 duration-200 overflow-hidden m-4 flex flex-col max-h-[calc(100vh-2rem)]',
  {
    variants: {
      size: {
        sm: 'max-w-sm',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        '2xl': 'max-w-5xl',
        '3xl': 'max-w-6xl',
        '4xl': 'max-w-7xl',
        '5xl': 'max-w-[90rem]',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  }
)

export interface ModalProps extends VariantProps<typeof modalContentVariants> {
  isOpen: boolean
  onClose: () => void
  title: string
  children: ReactNode
  footer?: ReactNode
  className?: string
}

export function Modal({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size,
  className,
}: ModalProps) {
  const headerRef = useRef<HTMLDivElement>(null)
  const { swipeHandlers, swipeOffset, isDragging } = useSwipeDown(onClose, {
    dragHandleRef: headerRef,
  })

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    if (isOpen) {
      window.addEventListener('keydown', handleEscape)
    }
    return () => window.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  // Calculate opacity fade during drag
  const opacity = 1 - Math.min(swipeOffset / 200, 0.5)

  // Portal to <body>: the overlay is `position: fixed`, but a mounting site
  // inside any ancestor that creates a stacking context (e.g. the nav bar's
  // `relative z-10`) would clamp z-[110] to that ancestor's level and let page
  // content paint over the modal.
  return createPortal(
    <div className={modalOverlayVariants()} onClick={onClose}>
      <div
        className={cn(modalContentVariants({ size }), className)}
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
        style={{
          transform: `translateY(${Math.max(0, swipeOffset)}px)`,
          opacity,
          transition: isDragging ? 'none' : 'transform 0.2s ease-out, opacity 0.2s ease-out',
        }}
      >
        <div
          ref={headerRef}
          {...swipeHandlers}
          className="flex flex-col px-4 pt-2 pb-3 border-b border-white/5 bg-white/[0.02] shrink-0"
        >
          {/* Drag handle pill */}
          <div className="flex justify-center mb-1">
            <div className="w-8 h-1 rounded-full bg-white/10" />
          </div>
          {/* Title and close button */}
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-white/90">{title}</h2>
            <button
              onClick={onClose}
              className="text-white/30 hover:text-white/70 transition-colors"
            >
              <X size={16} />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto flex-1">{children}</div>
        {footer && (
          <div className="flex justify-end gap-3 px-4 py-3 border-t border-white/5 bg-white/[0.02] shrink-0">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}
